import {
  BASE_CURRENCY_CODE,
  COUNTRY_CURRENCY,
  CURRENCIES,
  DEFAULT_COUNTRY,
  FX_RATES_URL,
  fxRatesUrlForBase,
  GEO_PROVIDERS,
  TIMEZONE_COUNTRY,
} from "../config/currencyLocalization";

const GEO_CACHE_KEY = "connectsu_geo_country";
const RATES_CACHE_KEY = "connectsu_fx_gbp_v2";
const CACHE_TTL_MS = 60 * 60 * 1000;

const normalizeCurrencyCode = (code) =>
  String(code || BASE_CURRENCY_CODE)
    .trim()
    .toUpperCase();

const planRatesCacheKey = (baseCode) =>
  `connectsu_fx_base_${normalizeCurrencyCode(baseCode)}`;

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch (error) {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ value, expiresAt: Date.now() + CACHE_TTL_MS }),
    );
  } catch (error) {
    // Ignore storage failures (private mode, quota).
  }
};

export const getCurrencyForCountry = (countryCode) => {
  const normalized = String(countryCode || "")
    .trim()
    .toUpperCase();
  const currencyCode =
    COUNTRY_CURRENCY[normalized] || CURRENCIES[BASE_CURRENCY_CODE].code;
  return CURRENCIES[currencyCode] || CURRENCIES[BASE_CURRENCY_CODE];
};

export const detectCountryFromLocale = (language = "") => {
  const match = String(language).match(/[-_]([A-Za-z]{2})$/);
  if (!match) return null;
  const country = match[1].toUpperCase();
  return COUNTRY_CURRENCY[country] ? country : null;
};

export const detectCountryFromTimezone = (timeZone = "") => {
  return TIMEZONE_COUNTRY[timeZone] || null;
};

export const convertFromGbp = (amountGbp, rate) => {
  const numericAmount = Number(amountGbp) || 0;
  const numericRate = Number(rate);
  if (!numericRate || Number.isNaN(numericRate)) {
    return numericAmount;
  }
  return numericAmount * numericRate;
};

export const getCurrencyDecimalPlaces = (currencyCode) => {
  const code = normalizeCurrencyCode(currencyCode);
  if (code === "JPY") {
    return 0;
  }
  return 2;
};

export const roundCurrencyAmount = (value, currencyCode) => {
  const decimals = getCurrencyDecimalPlaces(currencyCode);
  const factor = 10 ** decimals;
  const numericValue = Number(value) || 0;
  return Math.round((numericValue + Number.EPSILON) * factor) / factor;
};

const getGbpRateFactor = (currencyCode, rates) => {
  const code = normalizeCurrencyCode(currencyCode);
  if (code === BASE_CURRENCY_CODE) {
    return 1;
  }
  const rate = Number(rates[code]);
  if (!rate || Number.isNaN(rate)) {
    return null;
  }
  return rate;
};

/** Rates are relative to GBP (1 GBP = rates[USD] USD, etc.). */
export const convertBetweenCurrencies = (
  amount,
  fromCode,
  toCode,
  rates = {},
) => {
  const numericAmount = Number(amount) || 0;
  const from = normalizeCurrencyCode(fromCode);
  const to = normalizeCurrencyCode(toCode);

  if (from === to) {
    return roundCurrencyAmount(numericAmount, to);
  }

  const fromFactor = getGbpRateFactor(from, rates);
  const toFactor = getGbpRateFactor(to, rates);

  if (fromFactor == null || toFactor == null) {
    return roundCurrencyAmount(numericAmount, from);
  }

  const converted = (numericAmount * toFactor) / fromFactor;
  return roundCurrencyAmount(converted, to);
};

/** Rates from open.er-api latest/{planCurrency}: 1 plan unit = rates[target] target. */
export const convertUsingPlanBaseRates = (
  amount,
  toCode,
  planBaseRates = {},
) => {
  const numericAmount = Number(amount) || 0;
  const to = normalizeCurrencyCode(toCode);
  const targetRate = Number(planBaseRates[to]);

  if (!targetRate || Number.isNaN(targetRate)) {
    return roundCurrencyAmount(numericAmount, to);
  }

  return roundCurrencyAmount(numericAmount * targetRate, to);
};

export const formatLocalizedAmount = (amount, currency) => {
  const config = currency || CURRENCIES[BASE_CURRENCY_CODE];
  const rounded = roundCurrencyAmount(amount, config.code);

  try {
    const decimals = getCurrencyDecimalPlaces(config.code);
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(rounded);
  } catch (error) {
    return `${config.symbol}${rounded.toFixed(getCurrencyDecimalPlaces(config.code))}`;
  }
};

const fetchWithTimeout = async (url, timeoutMs = 4000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchJson = async (url, timeoutMs = 4000) => {
  const response = await fetchWithTimeout(url, timeoutMs);
  return response.json();
};

const fetchText = async (url, timeoutMs = 4000) => {
  const response = await fetchWithTimeout(url, timeoutMs);
  return response.text();
};

export const parseCountryCode = (payload) => {
  if (!payload) return null;

  if (typeof payload === "string") {
    const locMatch = payload.match(/(?:^|\n)loc=([A-Za-z]{2})(?:\n|$)/);
    if (locMatch) return locMatch[1].toUpperCase();
    const trimmed = payload.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(trimmed) ? trimmed : null;
  }

  if (payload.error || payload.success === false) return null;

  const raw =
    payload.country_code ||
    payload.countryCode ||
    (typeof payload.country === "string" && payload.country.length === 2
      ? payload.country
      : null);

  if (!raw) return null;
  const normalized = String(raw).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
};

export const fetchCountryFromIp = async () => {
  const cached = readCache(GEO_CACHE_KEY);
  if (cached) return cached;

  for (const provider of GEO_PROVIDERS) {
    try {
      const payload =
        provider.type === "text"
          ? await fetchText(provider.url)
          : await fetchJson(provider.url);
      const countryCode = parseCountryCode(payload);
      if (countryCode) {
        writeCache(GEO_CACHE_KEY, countryCode);
        return countryCode;
      }
    } catch (error) {
      // Try the next free provider if this one is rate-limited or blocked.
    }
  }

  throw new Error("Country lookup failed");
};

export const fetchGbpRates = async () => {
  const cached = readCache(RATES_CACHE_KEY);
  if (cached) return cached;

  const data = await fetchJson(FX_RATES_URL);
  const rates = data?.rates;
  if (!rates || typeof rates !== "object") {
    throw new Error("FX lookup failed");
  }
  writeCache(RATES_CACHE_KEY, rates);
  return rates;
};

export const fetchRatesForBase = async (baseCurrencyCode) => {
  const baseCode = normalizeCurrencyCode(baseCurrencyCode);
  const cacheKey = planRatesCacheKey(baseCode);
  const cached = readCache(cacheKey);
  if (cached) return cached;

  const data = await fetchJson(fxRatesUrlForBase(baseCode));
  const rates = data?.rates;
  if (!rates || typeof rates !== "object") {
    throw new Error("FX lookup failed");
  }
  writeCache(cacheKey, rates);
  return rates;
};

export const detectCountry = async () => {
  try {
    return await fetchCountryFromIp();
  } catch (error) {
    const fromLocale = detectCountryFromLocale(
      typeof navigator !== "undefined" ? navigator.language : "",
    );
    if (fromLocale) return fromLocale;

    const timeZone =
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "";
    return detectCountryFromTimezone(timeZone) || DEFAULT_COUNTRY;
  }
};

export const resolveLocalizedCurrency = async () => {
  const countryCode = await detectCountry();
  const currency = getCurrencyForCountry(countryCode);
  let rates = {};

  try {
    rates = await fetchGbpRates();
  } catch (error) {
    return {
      countryCode,
      currency: CURRENCIES[BASE_CURRENCY_CODE],
      rate: 1,
      rates: {},
    };
  }

  const rate =
    currency.code === BASE_CURRENCY_CODE
      ? 1
      : Number(rates[currency.code]) || 1;

  return { countryCode, currency, rate, rates };
};
